import { useMemo, useState } from 'react';
import { Flex } from 'antd';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useClassifierFormLabelTranslation } from '../form/useClassifierFormLabelTranslation';
import {
  CategoryFormDataType,
  getValidPeriodDisplayValueWithFormValueAttached,
  Table,
  TextEllipsis,
} from '@atlas-frontend/sdk';
import styled from 'styled-components';
import { CategoryGroupEntries } from './CategoryGroupEntries';
import { CategoryRelationsViewModal } from './CategoryRelationsViewModal';

export interface CategoryDetailTableProps {
  name: 'categories' | 'categoryGroups' | 'combinedGroups';
  categories?: CategoryFormDataType[];
}

const columnHelper = createColumnHelper<CategoryFormDataType>();

export const CategoryDetailTable = (props: CategoryDetailTableProps) => {
  const { name, categories } = props;
  const { t: tLabel } = useClassifierFormLabelTranslation();

  const [modalRowIndex, setModalRowIndex] = useState<number | null>(null);

  const newColumns = useMemo(() => {
    const columns: ColumnDef<CategoryFormDataType, any>[] = [
      columnHelper.accessor('sequenceNumber', {
        header: tLabel('categories.sequenceNumber.label'),
        cell: (props) => <TextEllipsis>{props.renderValue()}</TextEllipsis>,
        meta: {
          width: 110,
          className: 'sticky-left',
          textAlign: 'right',
        },
        filterFn: 'includesString',
        sortingFn: 'basic',
      }),
      columnHelper.accessor('code', {
        header: tLabel('categories.code.label'),
        cell: (props) => <TextEllipsis>{props.renderValue()}</TextEllipsis>,
        meta: {
          width: '20ch',
          className: 'sticky-left',
        },
      }),
      columnHelper.accessor('name', {
        header: tLabel('categories.name.label'),
        cell: (props) => <TextEllipsis>{props.renderValue()}</TextEllipsis>,
        meta: {
          width: '60%',
          minWidth: 150,
        },
      }),
      columnHelper.accessor('nameEng', {
        header: tLabel('categories.nameEng.label'),
        cell: (props) => <TextEllipsis>{props.renderValue()}</TextEllipsis>,
        meta: {
          width: '30ch',
        },
      }),
      columnHelper.accessor('description', {
        header: tLabel('categories.description.label'),
        cell: (props) => <TextEllipsis>{props.renderValue()}</TextEllipsis>,
        meta: {
          width: '40%',
          minWidth: 150,
        },
      }),
      columnHelper.accessor(
        (rowData) =>
          getValidPeriodDisplayValueWithFormValueAttached(rowData.validPeriod),
        {
          id: 'validPeriod',
          header: tLabel('categories.validPeriod.label'),
          cell: (props) => <TextEllipsis>{props.renderValue()}</TextEllipsis>,
          meta: {
            width: 200,
          },
          filterFn: 'validPeriod',
        },
      ),
    ];

    if (name === 'categoryGroups' || name === 'combinedGroups') {
      columns.push(
        columnHelper.accessor('categoryRelations', {
          header: tLabel('categories.categoryRelations.label'),
          cell: (props) => (
            <CategoryGroupEntries
              id={`category-group-entries-${props.row.index}`}
              entries={props.getValue()}
              onClick={() => setModalRowIndex(props.row.index)}
            />
          ),
          meta: {
            width: 100,
          },
        }),
      );
    }

    return columns;
  }, [tLabel, name]);

  const modalCategoryData =
    modalRowIndex !== null && categories?.[modalRowIndex];

  return (
    <CategoryDetailTableWrapper vertical gap="large" align="end">
      <Table
        name="categoryDetailTable"
        data={categories || []}
        columns={newColumns}
        defaultSort={[{ id: 'sequenceNumber', desc: false }]}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
      />
      {modalCategoryData && (
        <CategoryRelationsViewModal
          name={name}
          categoryData={modalCategoryData}
          onClose={() => setModalRowIndex(null)}
        />
      )}
    </CategoryDetailTableWrapper>
  );
};

const CategoryDetailTableWrapper = styled(Flex)`
  width: 100%;
`;
